import React, { useEffect, useRef, useState } from 'react';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import { useTranslations } from 'next-intl';
import { FieldError, useForm } from 'react-hook-form';
import Button from '@/ui/components/shared/Button';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Image from 'next/image';
import { submitCSForm } from '@/server-functions/csForm/submitCSForm';
import ContactOptions from '@/ui/components/tiles/CustomerSupportForm/components/ContactOptions';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { FRIENDLY_CAPTCHA_ENDPOINT } from '@/integrations/captcha/constants';
import ConfirmationPage from '@/ui/components/tiles/CustomerSupportForm/components/ConfirmationPage';
import { PrefillFormValues } from '@/ui/components/tiles/CustomerSupportForm/types/prefillFormValues';
import OpenLiveChatAndSendMessage from '@/ui/components/tiles/CustomerSupportForm/components/LiveChat';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import ConfirmationPagePhone from '@/ui/components/tiles/CustomerSupportForm/components/ConfirmationPagePhone';
import DeviceTypeEnum from '@/integrations/serviceAPI/internal-tools/enums/device-type-enum';
import { getDeviceDetails } from '@/server-functions/serviceApiServiceWrapper';
import { DeviceDetailsResponse } from '@/ui/components/tiles/CustomerSupportForm/types/deviceDetailsResponse';
import { buildTrackingId } from '@/utils/tracking-helper';
import UploadIcon from '../../../../../../public/images/upload-icon.svg';
import RemoveIcon from '../../../../../../public/images/remove-icon.svg';

export default function RequestForm(
  props: Readonly<{
    components: any;
    prefillValues: PrefillFormValues;
    formPath: string[];
    formUrl: string;
    contactOptions: string[];
    existingTicketId?: number;
    trackingPrefix?: string;
  }>,
) {
  const t = useTranslations('CSForm');
  const locale = useLocalization();
  const [attachments, setAttachments] = useState<Map<string, File[]>>(new Map());
  const [contactOption, setContactOption] = useState<string>('');
  const [formSchema, setFormSchema] = useState<yup.ObjectSchema<yup.AnyObject>>();
  const [submitEnabled, setSubmitEnabled] = useState(true); // TODO set to false
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const [hasError, setError] = useState<boolean>(false);
  const [ticketId, setTicketId] = useState<number | undefined>(props.existingTicketId);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { components, prefillValues, formPath, contactOptions, trackingPrefix } = props;

  const fields = components.find((c: { id: string }) => c.id === 'custom-fields');
  const lifecycle = components.find((c: { id: string }) => c.id === 'lifecycle');
  const ucSecurityCardColor = components.find((c: { id: string }) => c.id === 'security-card');

  const labelTranslations = components
    .find((c: { id: string }) => c.id === 'translations')
    .content.sections.find((c: { title: string }) => c.title === 'Labels');
  const descriptionTranslations = components
    .find((c: { id: string }) => c.id === 'translations')
    .content.sections.find((c: { title: string }) => c.title === 'Descriptions');

  const mandatoryFields = components.find((c: { id: string }) => c.id === 'fields-mandatory');
  const mandatoryOneOf = new Map<String, Array<String>>();
  const mandatoryOneOfAllCombinations = new Map<String, Array<String>>();

  const validatedDeviceTypeRef = useRef<Map<string, any>>(new Map());

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'none',
    language: locale.language as any,
    showAttribution: true,
  });

  useEffect(() => {
    if (captchaHook && !captchaHook.captchaStatus.error && captchaHook.captchaStatus.solution) {
      setSubmitEnabled(true);
    }
  }, [captchaHook.captchaStatus]);

  useEffect(() => {
    document.getElementById('csform-scrolltop')?.scrollIntoView({ behavior: 'smooth' });
  }, [contactOption]);

  components.forEach(
    (c: { id: string; content: { chunks: { content: { options: { key: String }[] } }[][] } }) => {
      if (c.id === 'fields-mandatory-one-of') {
        c.content?.chunks?.forEach((chunk: { content: { options: { key: String }[] } }[]) => {
          const oneOf = new Array<String>();

          chunk[0]?.content?.options?.forEach((o: { key: String }) => {
            oneOf.push(o.key);
          });

          mandatoryOneOf.set(oneOf[0], oneOf);

          oneOf.forEach((key) => {
            mandatoryOneOfAllCombinations.set(key, oneOf);
          });
        });
      }
    },
  );

  const labelTranslationsMap = new Map<string, string>();
  labelTranslations.properties?.forEach((l: { key: string; value: string }) => {
    labelTranslationsMap.set(l.key, l.value);
  });
  const descriptionTranslationsMap = new Map<string, string>();
  descriptionTranslations?.properties?.forEach((l: { key: string; value: string }) => {
    descriptionTranslationsMap.set(l.key, l.value);
  });

  const fieldsArr = Array.from(fields.content.options).map((o: any) => {
    const value = JSON.parse(o.value);
    return {
      key: o.key,
      type: value.type,
      zendeskId: value.zendeskId,
      isDevice: value.isDevice,
    };
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    // @ts-ignore
  } = useForm<yup.InferType<yup.AnyObject>>({
    // @ts-ignore
    resolver: yupResolver(formSchema),
    defaultValues: {
      firstName: prefillValues.firstName || '',
      lastName: prefillValues.lastName || '',
      email: prefillValues.email || '',
      description: prefillValues.ticketDescription || '',
      smartLockId: prefillValues.smartLockId || '',
    },
  });

  const mandatoryFieldsSet = new Set<string>();
  mandatoryFields.content.options?.forEach((o: { key: string }) => {
    mandatoryFieldsSet.add(o.key);
  });

  const checkDeviceIdValidityAndGetType = async (
    deviceId: string,
    fieldKey: string,
  ): Promise<DeviceDetailsResponse> => {
    let deviceType: DeviceTypeEnum;
    switch (fieldKey) {
      case 'smartLockId':
        deviceType = DeviceTypeEnum.SMART_LOCK_UNKNOWN;
        break;
      case 'openerId':
        deviceType = DeviceTypeEnum.OPENER;
        break;
      case 'doorSensorId':
        deviceType = DeviceTypeEnum.DOOR_SENSOR;
        break;
      case 'fobId':
        deviceType = DeviceTypeEnum.FOB;
        break;
      case 'smartDoorId':
        deviceType = DeviceTypeEnum.SMART_DOOR;
        break;
      case 'boxId':
        deviceType = DeviceTypeEnum.BOX;
        break;
      case 'keypadId':
        deviceType = DeviceTypeEnum.KEYPAD;
        break;
      default:
        deviceType = DeviceTypeEnum.SMART_LOCK_UNKNOWN;
        break;
    }

    return getDeviceDetails(deviceId, deviceType, 1);
  };

  useEffect(() => {
    const fs = yup.object({
      firstName: yup
        .string()
        .required(t('firstNameRequired'))
        .max(50, t('Maximum 50 characters allowed')),
      lastName: yup
        .string()
        .required(t('lastNameRequired'))
        .max(50, t('Maximum 50 characters allowed')),
    });

    const dynamicFieldsShape: Record<string, yup.AnySchema> = {};
    if (contactOption === 'email' || contactOption === 'chat') {
      dynamicFieldsShape.email = yup.string().required(t('EmailRequired')).email(t('InvalidEmail'));
    }

    if (contactOption === 'callback') {
      dynamicFieldsShape.phone = yup.string().required(t('PhoneRequired'));
    }

    fieldsArr.forEach((field) => {
      switch (field.type) {
        case 'text':
        case 'textarea':
        case 'email':
          dynamicFieldsShape[field.key] = yup.string();
          if (field.isDevice) {
            dynamicFieldsShape.deviceId = yup.string();
          }
          break;
        case 'fileUpload':
          if (mandatoryFieldsSet.has(field.key)) {
            dynamicFieldsShape[field.key] = yup.mixed().test({
              test: () => (attachments.get(field.key)?.length || 0) > 0,
              message: t('attachmentsUploaded'),
            });
          }
          break;
        default:
          dynamicFieldsShape[field.key] = yup.mixed();
      }

      if (mandatoryFieldsSet.has(field.key)) {
        if (field.type === 'text' || field.type === 'textarea' || field.type === 'customField') {
          dynamicFieldsShape[field.key] = dynamicFieldsShape[field.key].required(
            t(`${field.key}Required`),
          );
        } else if (field.type === 'email') {
          dynamicFieldsShape[field.key] = yup
            .string()
            .email(t('InvalidEmail'))
            .required(t(`${field.key}Required`));
        }
      }

      if (mandatoryOneOf.has(field.key)) {
        const oneOfFields = mandatoryOneOf.get(field.key);

        if (oneOfFields) {
          const reducedOneOfFields = (oneOfFields as string[]).filter((v) => v !== field.key);
          dynamicFieldsShape[field.key] = yup.string().when(reducedOneOfFields, {
            is: (...values: any[]) => values.some((v) => v && v !== ''),
            then: (schema) => schema,
            otherwise: (schema) => schema.required(t('oneFieldRequired')),
          });
        }
      }
    });

    const deviceIdFields = fieldsArr.filter((f) => f.isDevice);

    // in case contains deviceId add validation for it
    const deviceIdValidationSchema = yup
      .string()
      .optional()
      .transform((value) => (!value ? undefined : value))
      .max(8, t('error_invalidDeviceId_tooLong'))
      .min(8, t('error_invalidDeviceId_tooShort'))
      .test('is-alphanumeric-device-id', t('error_invalidDeviceId_notAlphanumeric'), (value) => {
        // optional
        if (!value) {
          return false;
        }

        return /^[a-zA-Z0-9]+$/.test(value);
      })
      .test('api-check-if-valid', t('error_invalidDeviceId'), async (value, context) => {
        // if empty, okay
        if (!value) {
          return true;
        }

        // "yup"...need to add checks here too, otherwise will call api
        const isLengthInvalid = value.length < 7 && value.length > 8;
        const isFormatInvalid = !/^[a-zA-Z0-9]+$/.test(value);

        // We return true here on purpose, other checks check this.
        // This is to prevent unnecessary calls to API.
        if (isLengthInvalid || isFormatInvalid) {
          return false;
        }

        try {
          const response = await checkDeviceIdValidityAndGetType(value, context.path);
          if (response.isValid && response.deviceTypeInfo) {
            validatedDeviceTypeRef.current.set(context.path, response.deviceTypeInfo);
            return true;
          }
          return false;
        } catch (e) {
          console.error('Smartlock API check failed:', e);

          return false;
        }
      });

    deviceIdFields.forEach((field) => {
      if (dynamicFieldsShape[field.key]) {
        // to keep it mandatory
        dynamicFieldsShape[field.key] =
          dynamicFieldsShape[field.key].concat(deviceIdValidationSchema);
      } else {
        dynamicFieldsShape[field.key] = deviceIdValidationSchema;
      }
    });

    const dynamicFieldsSchema = yup.object().shape(dynamicFieldsShape);
    const mergedSchema = dynamicFieldsSchema.concat(fs);
    setFormSchema(mergedSchema);
  }, [contactOption]);

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    // @ts-ignore
    const { key } = e.currentTarget;
    const droppedFiles = Array.from(e.dataTransfer.files);
    setAttachments((prev) => {
      const newMap = new Map(prev);
      const existingFiles = newMap.get(key) || [];
      newMap.set(key, [...existingFiles, ...droppedFiles]);
      return newMap;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const key = (e.currentTarget as HTMLElement).id;
      const selectedFiles = Array.from(e.target.files);
      setAttachments((prev) => {
        const newMap = new Map(prev);
        const existingFiles = newMap.get(key) || [];
        newMap.set(key, [...existingFiles, ...selectedFiles]);
        return newMap;
      });
    }
  };

  const removeFile = (index: number, fieldName: string) => {
    setAttachments((prev) => {
      const newMap = new Map(prev);
      const files = newMap.get(fieldName) || [];
      files.splice(index, 1);
      newMap.set(fieldName, files);
      return newMap;
    });
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);

    try {
      const lifecycleSelection: { lifecycle: string; boughtFrom?: string } = {
        lifecycle: 'unknown',
      };

      if (lifecycle.content?.selectedComponent?.id === 'lifecycle-after-sales') {
        lifecycleSelection.lifecycle = 'after-sales';
        lifecycleSelection.boughtFrom =
          lifecycle.content?.selectedComponent?.content?.options[0].value;
      } else if (lifecycle.content?.selectedComponent?.id === 'lifecycle-pre-sales') {
        lifecycleSelection.lifecycle = 'pre-sales';
      }

      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      validatedDeviceTypeRef.current.forEach((deviceType, fieldKey) => {
        formData.append(`validatedDeviceType_${fieldKey}`, deviceType);
      });

      let i = 0;

      attachments.forEach((files) => {
        files.forEach((file) => {
          formData.append(`attachments[${i}][file]`, file);
          i += 1;
        });
      });

      if (ucSecurityCardColor && ucSecurityCardColor.content?.options[0]?.key !== '-') {
        formData.append('securityCardColor', ucSecurityCardColor.content?.options[0]?.value);
      }

      let formPathCleaned = formPath.join(' ');
      formPathCleaned = formPathCleaned.slice(0, -2); // to remove the last '->'

      const response = await submitCSForm({
        formData,
        fields: fieldsArr,
        lifecycle: lifecycleSelection,
        language: locale.language,
        captchaSolution: captchaHook.captchaStatus.solution || '',
        formPath: formPathCleaned,
        formUrl: props.formUrl,
        existingTicketId: props?.existingTicketId,
      });

      if (response.success) {
        setTicketId(response.ticketId);
        setName(`${formData.get('firstName')} ${formData.get('lastName')}`);
        setEmail(`${formData.get('email')}`);
        setSuccessfullySubmitted(true);
      } else {
        setError(true);
        captchaHook.resetWidget();
      }
    } catch (e) {
      console.error('Error submitting form:', e);
    } finally {
      setIsLoading(false);
    }
  };

  if (!contactOption || contactOption === '') {
    return <ContactOptions options={contactOptions} setOption={setContactOption} />;
  }

  if (successfullySubmitted) {
    return (
      <>
        {contactOption === 'phone' ? (
          <ConfirmationPagePhone ticketId={ticketId!} />
        ) : (
          <ConfirmationPage />
        )}
        {contactOption === 'chat' && (
          <OpenLiveChatAndSendMessage ticketId={ticketId} name={name} email={email} />
        )}
      </>
    );
  }

  const groupedFields: Record<string, typeof fieldsArr> = {};
  fieldsArr.forEach((field) => {
    const groupKey = mandatoryOneOfAllCombinations.get(field.key)?.sort().join(',') || field.key;
    if (!groupedFields[groupKey]) {
      groupedFields[groupKey] = [];
    }
    groupedFields[groupKey].push(field);
  });

  const oneOffGroupRendered = new Map<string, number>();

  return (
    <div className="mx-auto my-8 flex max-w-screen-2xl grow justify-center overflow-x-hidden px-4 sm:my-14 sm:px-8 lg:my-16">
      <form
        className="grid max-w-[56rem] grow grid-cols-1 gap-y-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2 className="text-5xl font-normal">
          {t(`header_thanksForSharing_${contactOption}`)} <br />
          <span className="font-bold">
            {formatPunctuation(t(`header_justLastOneStep_${contactOption}`))}
          </span>
        </h2>
        <p>{t(`header_description_${contactOption}`)}</p>
        <div className="grid gap-10 md:grid-cols-2">
          <TextInput
            id="firstName"
            srLabel={t('firstName')}
            label={`${t('firstName')} *`}
            placeholder={t('firstName')}
            register={register}
            errors={errors.firstName as FieldError}
          />
          <TextInput
            id="lastName"
            srLabel={t('lastName')}
            label={`${t('lastName')} *`}
            placeholder={t('lastName')}
            register={register}
            errors={errors.lastName as FieldError}
          />
        </div>
        <div className="grid gap-10">
          {contactOption === 'callback' && (
            <TextInput
              id="phone"
              srLabel={t('phone')}
              label={`${t('phone')} *`}
              placeholder={t('phone')}
              register={register}
              errors={errors.phone as FieldError}
            />
          )}
          {contactOption !== 'callback' && (
            <TextInput
              id="email"
              srLabel={t('email')}
              label={`${t('email')} *`}
              placeholder={t('email')}
              register={register}
              errors={errors.email as FieldError}
            />
          )}
        </div>

        {Object.entries(groupedFields).map(([groupKey, group]) => {
          const firstField = group[0];
          let oneOffGroupTitle = null;
          if (
            mandatoryOneOfAllCombinations.has(firstField.key) &&
            (oneOffGroupRendered.get(groupKey) || 0) === 0
          ) {
            oneOffGroupTitle = (
              <div className="flex flex-wrap font-bold">{t('OneOfFollowingFieldsRequired')}</div>
            );
            oneOffGroupRendered.set(groupKey, 1);
          }

          const isOneOffGroup =
            group.length === 2 && mandatoryOneOfAllCombinations.has(firstField.key);

          return (
            <div key={groupKey} className="gap-0 align-bottom">
              {oneOffGroupTitle}
              {isOneOffGroup ? (
                <div className="flex items-end justify-between gap-10">
                  {group.map((field, index) => {
                    let label = labelTranslationsMap.get(field.key) ?? t(`${field.key}`);
                    if (mandatoryFieldsSet.has(field.key)) {
                      label = label.concat(' *');
                    }
                    const description = descriptionTranslationsMap.get(field.key) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: descriptionTranslationsMap.get(field.key) || '',
                        }}
                      />
                    ) : t.has(`${field.key}_description`) ? (
                      t(`${field.key}_description`)
                    ) : (
                      ''
                    );

                    if (field.type === 'fileUpload') {
                      return (
                        <div key={field.key} className="col-span-1">
                          {label && (
                            <label className="block text-[1rem] font-bold text-gray-900">
                              {label}
                            </label>
                          )}
                          {description && <label className="mb-2 block">{description}</label>}
                          <div
                            onDrop={handleFileDrop}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.add('bg-gray-100');
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.classList.remove('bg-gray-100');
                            }}
                            className={`relative rounded-lg border border-dashed ${
                              errors[field.key] ? 'border-red' : 'border-gray-300'
                            } bg-gray-50 p-8 text-center`}
                          >
                            <div className="flex justify-center">
                              <Image src={UploadIcon} alt="" width={32} height={32} />
                            </div>
                            <div className="mb-4">{t('Drag & Drop')}</div>
                            <label
                              htmlFor={field.key}
                              className="mb-4 cursor-pointer text-[1rem] text-gray-500 before:absolute before:inset-0"
                            >
                              {t('your files here or click to browse')}
                              <br />
                              {t('(JPEG, PNG, PDF with max size 20 MB)')}
                            </label>
                            <input
                              type="file"
                              onChange={handleFileSelect}
                              className="hidden"
                              id={field.key}
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                          </div>
                          {errors[field.key] && (
                            <p className="text-sm text-red">
                              {(errors[field.key] as FieldError).message as string}
                            </p>
                          )}
                          {attachments.get(field.key)?.map((attachment, index) => (
                            <div
                              key={index}
                              className="mt-2 flex items-center justify-between rounded border-b border-gray-100"
                            >
                              <div className="space-y-1 pb-2">
                                <span className="block text-[1rem] font-bold">
                                  {attachment.name}
                                </span>
                              </div>
                              <button type="button" onClick={() => removeFile(index, field.key)}>
                                <Image
                                  src={RemoveIcon}
                                  alt="Remove"
                                  width={32}
                                  height={32}
                                  className="text-red-500 h-5 w-5"
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <React.Fragment key={field.key}>
                        <div className="flex flex-1 flex-col">
                          <TextInput
                            id={field.key}
                            register={register}
                            errors={errors[field.key] as FieldError}
                            placeholder={label.replace('*', '')}
                            label={label}
                            srLabel={label}
                            type={field.type}
                            description={description}
                          />
                        </div>
                        {index === 0 && (
                          <div className="self-end pb-4 text-md font-medium">{t('or')}</div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div className="grid gap-10 md:grid-cols-1">
                  {group.map((field) => {
                    let label = labelTranslationsMap.get(field.key) ?? t(`${field.key}`);
                    if (mandatoryFieldsSet.has(field.key)) {
                      label = label.concat(' *');
                    }
                    const description = descriptionTranslationsMap.get(field.key) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: descriptionTranslationsMap.get(field.key) || '',
                        }}
                      />
                    ) : t.has(`${field.key}_description`) ? (
                      t(`${field.key}_description`)
                    ) : (
                      ''
                    );

                    if (field.type === 'fileUpload') {
                      return (
                        <div key={field.key} className="col-span-1">
                          {label && (
                            <label className="block text-[1rem] font-bold text-gray-900">
                              {label}
                            </label>
                          )}
                          {description && <label className="mb-2 block">{description}</label>}
                          <div
                            onDrop={handleFileDrop}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.add('bg-gray-100');
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.classList.remove('bg-gray-100');
                            }}
                            className={`relative rounded-lg border border-dashed ${
                              errors[field.key] ? 'border-red' : 'border-gray-300'
                            } bg-gray-50 p-8 text-center`}
                          >
                            <div className="flex justify-center">
                              <Image src={UploadIcon} alt="" width={32} height={32} />
                            </div>
                            <div className="mb-4">{t('Drag & Drop')}</div>
                            <label
                              htmlFor={field.key}
                              className="mb-4 cursor-pointer text-[1rem] text-gray-500 before:absolute before:inset-0"
                            >
                              {t('your files here or click to browse')}
                              <br />
                              {t('(JPEG, PNG, PDF with max size 20 MB)')}
                            </label>
                            <input
                              type="file"
                              onChange={handleFileSelect}
                              className="hidden"
                              id={field.key}
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png"
                            />
                          </div>
                          {errors[field.key] && (
                            <p className="text-sm text-red">
                              {(errors[field.key] as FieldError).message as string}
                            </p>
                          )}
                          {attachments.get(field.key)?.map((attachment, index) => (
                            <div
                              key={index}
                              className="mt-2 flex items-center justify-between rounded border-b border-gray-100"
                            >
                              <div className="space-y-1 pb-2">
                                <span className="block text-[1rem] font-bold">
                                  {attachment.name}
                                </span>
                              </div>
                              <button type="button" onClick={() => removeFile(index, field.key)}>
                                <Image
                                  src={RemoveIcon}
                                  alt="Remove"
                                  width={32}
                                  height={32}
                                  className="text-red-500 h-5 w-5"
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    if (['description', 'smartLockId'].includes(field.key)) {
                      return (
                        <div key={field.key} className="col-span-1">
                          <TextInput
                            id={field.key}
                            register={register}
                            errors={errors[field.key] as FieldError}
                            placeholder={label.toString().replace('*', '')}
                            label={label}
                            srLabel={label}
                            type={field.type}
                            description={description}
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={field.key} className="col-span-1 flex flex-col">
                        <TextInput
                          id={field.key}
                          register={register}
                          errors={errors[field.key] as FieldError}
                          placeholder={label.replace('*', '')}
                          label={label}
                          srLabel={label}
                          type={field.type}
                          description={description}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {captchaHook.CaptchaWidget({
          className: 'relative pb-4 max-w-[300px]',
        })}
        <Button
          type="submit"
          className="w-44 md:w-1/3"
          disabled={!submitEnabled || isLoading}
          data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-20 border-t-primary-100" />
            </div>
          ) : (
            t('submitRequest')
          )}
        </Button>
        {hasError && <p className="text-sm text-red">{t('submitFailed')}</p>}
      </form>
    </div>
  );
}
